import { HumanChallenge, type HumanVerification } from "@repo/verification";
import { apiProblemOf } from "@repo/api-client";

/**
 * The booking-abuse escalation outcomes the widget branches on (booking-abuse-protection). Read from the
 * RFC 9457 problem body (ADR-0008): `CHALLENGE_REQUIRED` carries the Turnstile `sitekey` + a `retryable`
 * flag (the challenge-outage case → "try again shortly"); `TEMPORARILY_BLOCKED` carries `retryAfterSeconds`.
 * Escalation-only — the happy path never reaches here (R1/R5).
 */
export type AbuseOutcome =
  | { kind: "challenge"; sitekey: string; retryable: boolean }
  | { kind: "blocked"; retryAfterSeconds: number }
  | null;

export function classifyAbuseProblem(err: unknown): AbuseOutcome {
  const p = apiProblemOf(err) as (Record<string, unknown> & { errorCode?: string }) | undefined;
  if (!p) return null;
  if (p.errorCode === "CHALLENGE_REQUIRED") {
    return {
      kind: "challenge",
      sitekey: typeof p.sitekey === "string" ? p.sitekey : "",
      retryable: p.retryable === true,
    };
  }
  if (p.errorCode === "TEMPORARILY_BLOCKED") {
    return {
      kind: "blocked",
      retryAfterSeconds: typeof p.retryAfterSeconds === "number" ? p.retryAfterSeconds : 0,
    };
  }
  return null;
}

/**
 * The challenge interstitial the DETAILS step swaps in on `CHALLENGE_REQUIRED`. When the challenge is
 * solvable it renders the vendor-neutral `<HumanChallenge>` (Turnstile behind `@repo/verification`) and
 * resubmits via [onToken]; when the verifier is DOWN ([retryable]) it shows "try again shortly" + a retry
 * — NEVER a silent pass (R1). Consent-free: no cookie, no identifier.
 */
export function ChallengeInterstitial({
  sitekey,
  retryable,
  language,
  onToken,
  onRetry,
  verification,
  labels,
}: {
  sitekey: string;
  retryable: boolean;
  language?: string;
  onToken: (token: string) => void;
  onRetry: () => void;
  verification?: HumanVerification;
  labels: { prompt: string; retry: string; retryCta: string };
}) {
  if (retryable) {
    return (
      <div className="rsv-challenge" role="alert" data-testid="challenge-retry">
        <p>{labels.retry}</p>
        <button type="button" onClick={onRetry}>
          {labels.retryCta}
        </button>
      </div>
    );
  }
  return (
    <div className="rsv-challenge" data-testid="challenge">
      <p>{labels.prompt}</p>
      <HumanChallenge
        sitekey={sitekey}
        language={language}
        onVerified={onToken}
        onError={onRetry}
        verification={verification}
      />
    </div>
  );
}
